import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, LogOut, Shield } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { Button } from "../components/ui/button";
import NotificationsBell from "./NotificationsBell";
import AuthDialog from "./AuthDialog";

const Header = () => {
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState("login");
  const nav = useNavigate();

  const openAuth = (t) => { setAuthTab(t); setAuthOpen(true); };

  return (
    <header
      className="sticky top-0 z-40 glass border-b border-sky-100"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
          <span className="w-10 h-10 rounded-xl bg-[#032B44] grid place-items-center overflow-hidden group-hover:ring-2 group-hover:ring-sky-200 transition-all">
            <img
              src="/icons/icon-192.png"
              alt="EasyIce"
              className="w-full h-full object-cover"
            />
          </span>
          <span className="font-heading text-lg tracking-tight font-semibold">
            EasyIce
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm">
          <NavLink to="/" end className={({isActive}) => isActive ? "text-[#032B44] font-medium" : "text-slate-600 hover:text-[#032B44]"} data-testid="nav-home">
            Home
          </NavLink>
          <NavLink to="/catalog" className={({isActive}) => isActive ? "text-[#032B44] font-medium" : "text-slate-600 hover:text-[#032B44]"} data-testid="nav-catalog">
            Prodotti
          </NavLink>
          {user && (
            <NavLink to="/orders" className={({isActive}) => isActive ? "text-[#032B44] font-medium" : "text-slate-600 hover:text-[#032B44]"} data-testid="nav-orders">
              Ordini
            </NavLink>
          )}
          {(user?.role === "admin" || user?.role === "staff") && (
            <NavLink to="/admin" className={({isActive}) => isActive ? "text-[#00A3FF] font-medium flex items-center gap-1" : "text-slate-600 hover:text-[#00A3FF] flex items-center gap-1"} data-testid="nav-admin">
              <Shield className="w-3.5 h-3.5" /> {user?.role === "staff" ? "Operatore" : "Admin"}
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-1">
          {(user?.role === "admin" || user?.role === "staff") && (
            <button
              onClick={() => nav("/admin")}
              className="md:hidden h-10 w-10 grid place-items-center rounded-lg hover:bg-sky-50 transition-colors"
              data-testid="mobile-nav-admin"
              aria-label={user?.role === "staff" ? "Operatore" : "Admin"}
              title={user?.role === "staff" ? "Operatore" : "Admin"}
            >
              <Shield className="w-5 h-5 text-[#00A3FF]" />
            </button>
          )}
          {user && (
            <button
              onClick={() => nav("/dashboard")}
              className="md:hidden h-10 w-10 grid place-items-center rounded-lg hover:bg-sky-50 transition-colors"
              data-testid="mobile-nav-dashboard"
              aria-label="Profilo"
              title="Profilo"
            >
              {user.picture ? (
                <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
              ) : (
                <User className="w-5 h-5 text-[#032B44]" />
              )}
            </button>
          )}
          {user && <NotificationsBell />}
          <button
            onClick={() => setOpen(true)}
            className="relative h-10 w-10 grid place-items-center rounded-lg hover:bg-sky-50 transition-colors"
            data-testid="cart-button"
            aria-label="Carrello"
          >
            <ShoppingBag className="w-5 h-5 text-[#032B44]" />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-[#00A3FF] text-white text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] grid place-items-center px-1" data-testid="cart-count">
                {count}
              </span>
            )}
          </button>

          {user ? (
            <div className="flex items-center gap-2">
              <div className="hidden sm:flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-sky-50">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                ) : (
                  <User className="w-4 h-4 text-sky-700" />
                )}
                <span className="text-xs font-medium text-[#032B44] max-w-[120px] truncate" data-testid="user-name">{user.name}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => { await logout(); nav("/"); }}
                data-testid="logout-button"
                className="text-slate-600"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => openAuth("login")}
                className="text-[#032B44] hover:bg-sky-50 rounded-lg"
                data-testid="login-button"
              >
                Accedi
              </Button>
              <Button
                onClick={() => openAuth("register")}
                className="bg-[#032B44] hover:bg-slate-800 text-white rounded-lg"
                data-testid="register-button"
              >
                Registrati
              </Button>
            </div>
          )}
        </div>
      </div>
      <AuthDialog open={authOpen} onClose={() => setAuthOpen(false)} initialTab={authTab} />
    </header>
  );
};

export default Header;
